import { ReactiveFormsModule } from '@angular/forms';
import { type AfterViewInit, ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { type BsModalRef, BsModalService, ModalModule } from 'ngx-bootstrap/modal';

import { EUserTariffStatus } from '@common/enums';

import { PlatformService } from '@common/services/platform.service';

import { EduHttpService } from '@web-builder/core/services/REST/edu.http.service';
import { TransferStateService } from '@web-builder/core/services/transfer-state.service';

import { type SignUpModalResult } from './edu-course-sign-up-modal';
import { take } from 'rxjs';

@Component({
    selector: 'web-modals',
    styleUrls: ['./modals.component.less'],
    templateUrl: './modals.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, TranslateModule, ModalModule],
})
export class ModalsComponent implements AfterViewInit {
    private readonly eduHttpService: EduHttpService = inject(EduHttpService);
    private readonly platformService: PlatformService = inject(PlatformService);
    private readonly modalService: BsModalService = inject(BsModalService);
    private readonly transferStateService: TransferStateService = inject(TransferStateService);

    private urlParams: URLSearchParams;

    public ngAfterViewInit(): void {
        if (this.platformService.isPlatformServer()) {
            return;
        }

        this.urlParams = new URLSearchParams(window.location.search);
        const action = this.urlParams.get('action');

        if (action === 'login') {
            this.openSignInModal();
        } else if (action === 'signup') {
            this.checkUserTariff();
        }
    }

    private openSignInModal(): void {
        const domainId = this.transferStateService.get('domainId');
        const courseId = this.urlParams.get('courseId');

        if (!domainId) {
            console.warn('Domain id is not defined');
            return;
        }

        // if (!courseId) {
        //     console.warn('Course id is not defined');
        //     return;
        // }
        import('./edu-course-sign-in-modal').then(({ EduCourseSignInModalComponent }) => {
            const modal = this.modalService.show(EduCourseSignInModalComponent, {
                animated: false,
                initialState: {
                    // courseId: Number(courseId),
                    domainId: Number(domainId),
                },
            });

            modal.content.onClose.pipe(take(1)).subscribe(({ message }) => {
                if (message === 'passwordRecovery') {
                    this.openPasswordRecoveryModal();
                }
            });
        });
    }

    public openPasswordRecoveryModal(): void {
        const domainId = this.transferStateService.get('domainId');

        import('./edu-course-password-recovery-modal').then(({ EduCoursePasswordRecoveryModalComponent }) => {
            const modal: BsModalRef = this.modalService.show(EduCoursePasswordRecoveryModalComponent, {
                animated: false,
                initialState: {
                    // eduCourse: this.course,
                    domainId,
                },
            });

            modal.content.onClose.pipe(take(1)).subscribe(({ message, email }) => {
                if (message === 'passwordRecoverySuccess') {
                    this.openPasswordRecoverySuccessModal(email);
                }
            });
        });
    }

    public openPasswordRecoverySuccessModal(email: string): void {
        import('./edu-course-password-recovery-success-modal').then(({ EduCoursePasswordRecoverySuccessModalComponent }) => {
            const modal: BsModalRef = this.modalService.show(EduCoursePasswordRecoverySuccessModalComponent, {
                animated: false,
                initialState: {
                    email,
                },
            });

            modal.content.onClose.pipe(take(1)).subscribe((result: boolean) => {
                if (result) {
                    this.openSignInModal();
                }
            });
        });
    }

    private openSignUpModal(): void {
        const domainId = this.transferStateService.get('domainId');
        const courseId = this.urlParams.get('courseId');

        if (!domainId) {
            console.warn('Domain id is not defined');
            return;
        }

        if (!courseId) {
            console.warn('Course id is not defined');
            return;
        }

        import('./edu-course-sign-up-modal').then(({ EduCourseSignUpModalComponent }) => {
            const modal = this.modalService.show(EduCourseSignUpModalComponent, {
                animated: false,
                initialState: {
                    userId: this.transferStateService.get('userId'),
                    courseId: Number(courseId),
                    domainId: Number(domainId),
                },
            });

            modal.content.onClose.pipe(take(1)).subscribe(({ data, message }: SignUpModalResult) => {
                if (data) {
                    this.openSignUpSuccessModal(data);
                    return;
                }

                if (message === 'signIn') {
                    this.openSignInModal();
                }
            });
        });
    }

    private checkUserTariff(): void {
        const userId = this.transferStateService.get('userId');

        if (!userId) {
            console.warn('User id is not defined');
            return;
        }

        this.eduHttpService.getUserTariff(userId).subscribe((status) => {
            if (status === EUserTariffStatus.blocked) {
                this.openTariffRestrictionsModal();
            } else {
                this.openSignUpModal();
            }
        });
    }

    public openSignUpSuccessModal(signUpFormData: SignUpModalResult['data']): void {
        import('./edu-course-sign-up-success-modal').then(({ EduCourseSignUpSuccessModalComponent }) => {
            const modal: BsModalRef = this.modalService.show(EduCourseSignUpSuccessModalComponent, {
                animated: false,
                initialState: { signUpFormData },
            });
            modal.content.onClose.pipe(take(1)).subscribe((isSuccess: boolean) => {
                if (!isSuccess) {
                    return;
                }
                this.openSignInModal();
            });
        });
    }

    private openTariffRestrictionsModal(): void {
        import('./edu-course-tariff-restrictions-modal').then(({ EduCourseTariffRestrictionsModalComponent }) => {
            this.modalService.show(EduCourseTariffRestrictionsModalComponent, { animated: false });
        });
    }
}
